import { useEffect, useState } from "react";
import {
  useGetOrganizationsQuery,
  useGetPlansQuery,
  useGetSubscriptionQuery,
  useCreateSubscriptionMutation,
  useUpdateSubscriptionMutation,
} from "../../../redux/api";
import { Modal } from "../../../components/Modal";
import { Field } from "../../../components/Field";
import { EntityMeta } from "../../../components/EntityMeta";
import { subscriptionStatusTone } from "../../../utils/status";
import { todayISO, addCycle, standardPriceFor, SOURCE_LABEL } from "./subscription-helpers";

interface SubscriptionFormModalProps {
  open: boolean;
  /** null = create */
  id: string | null;
  onClose: () => void;
  /** Pre-selects an organization when creating — set by whichever org-scoped
   * screen opened this (e.g. organization-subscriptions.tsx's "New
   * subscription" button), so the create form doesn't default to whichever
   * organization happens to be first in the list. */
  initialOrganizationId?: string;
}

type SubForm = {
  organizationId: string;
  planId: string;
  billingCycle: string;
  amountPaid: string;
  currency: string;
  status: string;
  autoRenew: string;
  startDate: string;
  endDate: string;
  paymentReference: string;
  remarks: string;
};

const emptyForm: SubForm = {
  organizationId: "",
  planId: "",
  billingCycle: "monthly",
  amountPaid: "0",
  currency: "INR",
  status: "pending",
  autoRenew: "no",
  startDate: todayISO(),
  endDate: todayISO(),
  paymentReference: "",
  remarks: "",
};

const n = (v: string) => Number(v) || 0;

// Platform Owner → Subscription add/edit popup. Activating/cancelling a
// subscription is done from the Actions column of whichever table this modal
// was opened from — not here, so this only ever creates or edits the plain
// fields.
export const SubscriptionFormModal = ({
  open,
  id,
  onClose,
  initialOrganizationId,
}: SubscriptionFormModalProps) => {
  const [activeId, setActiveId] = useState<string | null>(id);
  const isEdit = !!activeId;

  const { data: organizations = [] } = useGetOrganizationsQuery();
  const { data: plans = [] } = useGetPlansQuery();

  const { data: existing } = useGetSubscriptionQuery(activeId ?? "", {
    skip: !activeId,
  });

  const [createSubscription, { isLoading: creating }] = useCreateSubscriptionMutation();
  const [updateSubscription, { isLoading: updating }] = useUpdateSubscriptionMutation();

  const [form, setForm] = useState<SubForm>(emptyForm);
  const [baseline, setBaseline] = useState<SubForm>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  // Per-field required-field errors — only relevant on create (organization
  // and plan are locked/disabled once a subscription exists).
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});

  const saving = creating || updating;

  // Re-sync to the record being opened every time the popup opens (or the
  // target id changes while open) — this component stays mounted between
  // opens, so state from a previous visit would otherwise linger.
  useEffect(() => {
    if (!open) return;
    setActiveId(id);
    setFormError(null);
    setFieldErrors({});
    if (!id) {
      setForm({
        ...emptyForm,
        organizationId: initialOrganizationId || organizations[0]?.id || "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, id]);

  const selectedPlan = plans.find((p) => p.id === form.planId);
  const standardPrice = standardPriceFor(selectedPlan, form.billingCycle, form.currency);
  const isCustomPrice = standardPrice != null && n(form.amountPaid) !== standardPrice;

  // Pre-fill the plan's standard price as a starting point whenever the plan
  // or billing cycle changes on a NEW subscription — still freely editable
  // afterwards, this just saves having to go look the price up separately.
  useEffect(() => {
    if (isEdit || standardPrice == null) return;
    setForm((prev) => ({ ...prev, amountPaid: String(standardPrice) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, form.planId, form.billingCycle, form.currency]);

  useEffect(() => {
    if (!existing) return;
    const next: SubForm = {
      organizationId: existing.organizationId ?? "",
      planId: existing.planId ?? "",
      billingCycle: existing.billingCycle ?? "monthly",
      amountPaid: existing.amountPaid != null ? String(existing.amountPaid) : "0",
      currency: existing.currency ?? "INR",
      status: existing.status ?? "pending",
      autoRenew: existing.autoRenew ? "yes" : "no",
      startDate: (existing.startDate ?? todayISO()).slice(0, 10),
      endDate: (existing.endDate ?? todayISO()).slice(0, 10),
      paymentReference: existing.paymentReference ?? "",
      remarks: existing.remarks ?? "",
    };
    setForm(next);
    setBaseline(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing?.id, existing?.updatedAt]);

  const setField = (key: keyof SubForm, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // Changing the cycle (or start date) recomputes the end date — unless the
  // cycle is "custom", in which case the manually-entered end date is kept.
  const handleCycleChange = (billingCycle: string) =>
    setForm((prev) => ({
      ...prev,
      billingCycle,
      endDate:
        billingCycle === "custom"
          ? prev.endDate
          : addCycle(prev.startDate, billingCycle),
    }));

  const handleStartChange = (startDate: string) =>
    setForm((prev) => ({
      ...prev,
      startDate,
      endDate:
        prev.billingCycle === "custom"
          ? prev.endDate
          : addCycle(startDate, prev.billingCycle),
    }));

  const handleSave = async () => {
    setFormError(null);

    if (!isEdit) {
      const errors: Record<string, string> = {};
      if (!form.organizationId) errors.organizationId = "Organization is required.";
      if (!form.planId.trim()) errors.planId = "Plan is required.";
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        return;
      }
      setFieldErrors({});
    }

    try {
      if (isEdit && activeId) {
        await updateSubscription({
          id: activeId,
          input: {
            billingCycle: form.billingCycle,
            amountPaid: n(form.amountPaid),
            currency: form.currency,
            status: form.status,
            autoRenew: form.autoRenew === "yes",
            startDate: form.startDate,
            endDate: form.endDate,
            paymentReference: form.paymentReference.trim() || undefined,
            remarks: form.remarks.trim() || undefined,
          },
        }).unwrap();
      } else {
        await createSubscription({
          organizationId: form.organizationId,
          planId: form.planId.trim(),
          billingCycle: form.billingCycle,
          amountPaid: n(form.amountPaid),
          currency: form.currency,
          status: form.status,
          autoRenew: form.autoRenew === "yes",
          startDate: form.startDate,
          endDate: form.endDate,
          paymentReference: form.paymentReference.trim() || undefined,
          remarks: form.remarks.trim() || undefined,
        }).unwrap();
      }
      onClose();
    } catch (err) {
      setFormError(
        (err as { message?: string })?.message ??
          "Failed to save subscription.",
      );
    }
  };

  const orgName = (orgId: string) =>
    organizations.find((o) => o.id === orgId)?.name ?? orgId;

  const dirty = JSON.stringify(form) !== JSON.stringify(baseline);

  return (
    <Modal
      open={open}
      title={isEdit ? "Edit subscription" : "New subscription"}
      onClose={onClose}
      xwide
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleSave}
            disabled={saving || (isEdit && !dirty)}
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create subscription"}
          </button>
        </>
      }
    >
      <EntityMeta
        id={activeId ?? undefined}
        status={isEdit ? { label: form.status, tone: subscriptionStatusTone(form.status) } : undefined}
      />
      {isEdit && existing?.source ? (
        <div style={{ marginBottom: "1rem" }}>
          <span className="pill pill--neutral">
            {SOURCE_LABEL[existing.source] ?? existing.source}
          </span>
        </div>
      ) : null}

      {formError ? <div className="form-error">{formError}</div> : null}

      <div className="form-grid">
        <Field label="Organization" full error={fieldErrors.organizationId}>
          {isEdit ? (
            <input value={orgName(form.organizationId)} disabled />
          ) : (
            <select
              value={form.organizationId}
              onChange={(e) => {
                setField("organizationId", e.target.value);
                setFieldErrors((prev) => ({ ...prev, organizationId: undefined }));
              }}
            >
              {organizations.length === 0 ? (
                <option value="">No organizations</option>
              ) : (
                organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))
              )}
            </select>
          )}
        </Field>

        <Field label="Plan" error={fieldErrors.planId}>
          <select
            value={form.planId}
            onChange={(e) => {
              setField("planId", e.target.value);
              setFieldErrors((prev) => ({ ...prev, planId: undefined }));
            }}
            disabled={isEdit}
          >
            <option value="">— Select a plan —</option>
            {plans.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.code})
              </option>
            ))}
          </select>
        </Field>
        <label className="field">
          Currency
          <select
            value={form.currency}
            onChange={(e) => setField("currency", e.target.value)}
          >
            <option value="INR">INR</option>
            <option value="USD">USD</option>
          </select>
        </label>
        <label className="field">
          Billing cycle
          <select
            value={form.billingCycle}
            onChange={(e) => handleCycleChange(e.target.value)}
          >
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly (3 months)</option>
            <option value="half-yearly">Half-yearly (6 months)</option>
            <option value="yearly">Yearly</option>
            <option value="custom">Custom</option>
          </select>
        </label>
        <label className="field">
          Amount paid
          <input
            type="number"
            value={form.amountPaid}
            onChange={(e) => setField("amountPaid", e.target.value)}
          />
          {isCustomPrice ? (
            <span className="pill pill--warning">
              Custom price (plan list price: {form.currency} {standardPrice})
            </span>
          ) : null}
        </label>
        <label className="field">
          Start date
          <input
            type="date"
            value={form.startDate}
            onChange={(e) => handleStartChange(e.target.value)}
          />
        </label>
        <label className="field">
          End date
          <input
            type="date"
            value={form.endDate}
            onChange={(e) => setField("endDate", e.target.value)}
          />
          <span
            style={{
              fontSize: "0.75rem",
              color: "var(--text-faint)",
              fontWeight: 400,
            }}
          >
            Auto-set from billing cycle — editable.
          </span>
        </label>
        <label className="field">
          Status
          <select
            value={form.status}
            onChange={(e) => setField("status", e.target.value)}
          >
            <option value="pending">Pending</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="cancelled">Cancelled</option>
            <option value="suspended">Suspended</option>
          </select>
        </label>
        <label className="field">
          Auto-renew
          <select
            value={form.autoRenew}
            onChange={(e) => setField("autoRenew", e.target.value)}
          >
            <option value="no">No</option>
            <option value="yes">Yes</option>
          </select>
        </label>
        <label className="field">
          Payment reference
          <input
            value={form.paymentReference}
            onChange={(e) => setField("paymentReference", e.target.value)}
          />
        </label>
        <label className="field field--full">
          Remarks
          <textarea
            value={form.remarks}
            onChange={(e) => setField("remarks", e.target.value)}
          />
        </label>
      </div>
    </Modal>
  );
};
